// src/providers/analyticsPeriodProvider.tsx
//
// One period for the Dirección/v2 analytics screens, kept for the session: a
// director who picks October on Resumen still sees October on Cobros, and the
// «Preguntar» panel can change it from outside the screen (with an undo). It
// is deliberately not persisted — coming back next week to a stale period
// would read as wrong data. Screens with their own logic (Fit compares two
// periods, Estado looks at the last two days) keep a local period.
import React, { createContext, useContext, useMemo, useState } from 'react';
import { defaultPeriod, type PeriodValue } from '@/components/common/PeriodPicker';

interface AnalyticsPeriodContextValue {
  period: PeriodValue;
  setPeriod: (value: PeriodValue) => void;
}

const AnalyticsPeriodContext = createContext<AnalyticsPeriodContextValue | undefined>(undefined);

export const AnalyticsPeriodProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [period, setPeriod] = useState<PeriodValue>(() => defaultPeriod());
  const value = useMemo(() => ({ period, setPeriod }), [period]);
  return <AnalyticsPeriodContext.Provider value={value}>{children}</AnalyticsPeriodContext.Provider>;
};

/** `[period, setPeriod]`, shared by every analytics screen of the session. */
export const useAnalyticsPeriod = (): [PeriodValue, (value: PeriodValue) => void] => {
  const ctx = useContext(AnalyticsPeriodContext);
  if (!ctx) throw new Error('useAnalyticsPeriod must be used within an AnalyticsPeriodProvider');
  return [ctx.period, ctx.setPeriod];
};
